import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import { disableDocumentScroll, enableDocumentScroll } from '../../utils';
import React, { Component, Fragment, createRef } from 'react';
import { safeInvoke } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX, Z_INDEX_OVERLAY } from '../../constants';
import { CSSTransition } from 'react-transition-group';
import Backdrop from './Backdrop';
import Layer from '../Layer';
import Focuser from './Focuser';

/**
 * `Overlay` component.
 */
var overlays = [];

var isCurrentOverlay = function isCurrentOverlay(overlay) {
  return overlays[overlays.length - 1] === overlay;
};

var canSkip = function canSkip(node) {
  return node && node.dataset && node.dataset.skipAutoFocus;
};
/**
 * @since 0.0.65
 */


var Overlay =
/*#__PURE__*/
function (_Component) {
  _inherits(Overlay, _Component);

  function Overlay() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Overlay);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Overlay)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      isOpened: false,
      isExist: false
    });

    _defineProperty(_assertThisInitialized(_this), "focuser", new Focuser());

    _defineProperty(_assertThisInitialized(_this), "prevFocusElement", null);

    _defineProperty(_assertThisInitialized(_this), "contentRef", createRef());

    _defineProperty(_assertThisInitialized(_this), "backdropRef", createRef());

    _defineProperty(_assertThisInitialized(_this), "handleFocus", function (event) {
      if (!isCurrentOverlay(_assertThisInitialized(_this))) {
        return;
      }

      if (_this.focuser.canIgnoreFocusChange()) {
        return;
      }

      if (!_this.contentRef.current) {
        return;
      }

      if (_this.contentRef.current.contains(event.target)) {
        _this.lastFocus = event.target;
      } else if (event.target === _this.trapBeforeElement || event.target === _this.trapAfterElement) {
        _this.focuser.focusFirstDescendant(_this.contentRef.current);

        if (_this.lastFocus === document.activeElement) {
          _this.focuser.focusLastDescendant(_this.contentRef.current);
        }

        _this.lastFocus = document.activeElement;
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleBackdropClick", function () {
      var _this$props = _this.props,
          onClose = _this$props.onClose,
          canCloseOnBackdropClick = _this$props.canCloseOnBackdropClick;

      if (canCloseOnBackdropClick) {
        safeInvoke(onClose);
      } else {
        _this.focus();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleContentKeyUp", function (e) {
      var onClose = _this.props.onClose;

      if (!e.isDefaultPrevented() && e.key === 'Escape') {
        safeInvoke(onClose);
        e.preventDefault();
      }
    });

    return _this;
  }

  _createClass(Overlay, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (this.props.isOpen) {
        this.overlayWillOpen();
      }
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (!this.props.isOpen && prevProps.isOpen) {
        this.overlayWillClose();
      } else if (this.props.isOpen && !prevProps.isOpen) {
        this.overlayWillOpen();
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (this.props.isOpen) {
        this.overlayWillClose();
      }
    }
  }, {
    key: "overlayWillOpen",
    value: function overlayWillOpen() {
      var _this2 = this;

      disableDocumentScroll();
      overlays.push(this);
      document.addEventListener('focus', this.handleFocus, true);
      this.setState({
        isExist: true
      }, function () {
        _this2.prevFocusElement = document.activeElement;
      });
      setTimeout(function () {
        _this2.setState({
          isOpened: true
        }, function () {
          _this2.focus();
        });
      });
    }
  }, {
    key: "overlayWillClose",
    value: function overlayWillClose() {
      var _this3 = this;

      overlays.pop();

      if (overlays.length === 0) {
        enableDocumentScroll();
      }

      document.removeEventListener('focus', this.handleFocus, true);
      this.setState({
        isOpened: false
      }, function () {
        if (_this3.prevFocusElement) {
          _this3.prevFocusElement.focus();

          _this3.prevFocusElement = null;
        }
      });
    }
  }, {
    key: "focus",
    value: function focus() {
      if (this.contentRef.current === null || document.activeElement === null || !this.props.isOpen) {
        return;
      }

      var isFocusOutside = !this.contentRef.current.contains(document.activeElement);

      if (isFocusOutside) {
        var autofocusElement = this.contentRef.current.querySelector('[autofocus]');

        if (autofocusElement) {
          autofocusElement.focus();
        } else if (!this.focuser.focusFirstDescendant(this.contentRef.current, {
          canSkip: canSkip
        })) {
          this.contentRef.current.focus();
        }
      }
    }
  }, {
    key: "renderContent",
    value: function renderContent(_ref) {
      var _this4 = this;

      var baseClassName = _ref.baseClassName,
          children = _ref.children,
          canCloseOnEscapePress = _ref.canCloseOnEscapePress;
      return React.createElement(Fragment, null, React.createElement("div", {
        tabIndex: 0,
        ref: function ref(el) {
          _this4.trapBeforeElement = el;
        }
      }), React.createElement(CSSTransition, {
        in: this.state.isOpened,
        classNames: "".concat(baseClassName, "-"),
        timeout: 300,
        onExited: function onExited() {
          _this4.setState({
            isExist: false
          });
        },
        nodeRef: this.contentRef
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__content"),
        onKeyUp: canCloseOnEscapePress ? this.handleContentKeyUp : null,
        ref: this.contentRef,
        role: "dialog",
        "aria-modal": "true",
        tabIndex: -1
      }, children)), React.createElement("div", {
        tabIndex: 0,
        ref: function ref(el) {
          _this4.trapAfterElement = el;
        }
      }));
    }
  }, {
    key: "renderBackdrop",
    value: function renderBackdrop(_ref2) {
      var _this5 = this;

      var baseClassName = _ref2.baseClassName;
      return React.createElement(CSSTransition, {
        in: this.state.isOpened,
        classNames: "".concat(baseClassName, "-"),
        timeout: 300,
        nodeRef: this.backdropRef
      }, React.createElement(Backdrop, {
        onClick: function onClick() {
          return _this5.handleBackdropClick();
        },
        innerRef: this.backdropRef
      }));
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          baseClassName = _this$props2.baseClassName,
          className = _this$props2.className,
          children = _this$props2.children,
          isOpen = _this$props2.isOpen,
          placement = _this$props2.placement,
          size = _this$props2.size,
          onClose = _this$props2.onClose,
          canCloseOnBackdropClick = _this$props2.canCloseOnBackdropClick,
          canCloseOnEscapePress = _this$props2.canCloseOnEscapePress,
          props = _objectWithoutProperties(_this$props2, ["baseClassName", "className", "children", "isOpen", "placement", "size", "onClose", "canCloseOnBackdropClick", "canCloseOnEscapePress"]);

      var isExist = this.state.isExist;

      if (!isExist) {
        return null;
      }

      return React.createElement(Layer, {
        level: Z_INDEX_OVERLAY
      }, React.createElement("div", _extends({
        className: classNames(baseClassName, "".concat(baseClassName, "--").concat(placement), "".concat(baseClassName, "--").concat(size), className)
      }, props), this.renderBackdrop({
        baseClassName: baseClassName
      }), this.renderContent({
        baseClassName: baseClassName,
        children: children,
        canCloseOnEscapePress: canCloseOnEscapePress
      })));
    }
  }]);

  return Overlay;
}(Component);

_defineProperty(Overlay, "propTypes", {
  /**
   * Toggles visibility.
   */
  isOpen: PropTypes.bool,

  /**
   * Overlay placement.
   */
  placement: PropTypes.oneOf(['center', 'left', 'right']),

  /**
   * Overlay size.
   */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),

  /**
   * onClose handler.
   */
  onClose: PropTypes.func,

  /**
   * Indicating if clicking the backdrop should close the overlay.
   */
  canCloseOnBackdropClick: PropTypes.bool,

  /**
   * Indicating if pressing the escape key should close the overlay.
   */
  canCloseOnEscapePress: PropTypes.bool,

  /**
   * Content of the `Overlay`.
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
});

_defineProperty(Overlay, "defaultProps", {
  isOpen: false,
  placement: 'center',
  size: 'md',
  onClose: null,
  canCloseOnBackdropClick: true,
  canCloseOnEscapePress: true,
  children: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "overlay")
});

export { Overlay as default };